import { MessagePrimitive, useAuiState } from '@assistant-ui/react';
import React from 'react';

import { CollapsibleToolRun } from './CollapsibleToolRun.tsx';
import { groupParts, type PartSegment } from './group-parts.ts';
import { AssistantBubble } from '#plugin-chat/components/bubble/AssistantBubble.tsx';
import { ProgressBubble } from '#plugin-chat/components/thread/ProgressBubble.tsx';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { isErroredToolPart } from '#plugin-chat/hooks/isErroredToolPart.ts';
import { useIsCollapsibleTool } from '#plugin-chat/hooks/useIsCollapsibleTool.ts';

type GroupablePart = {
  type: string;
  toolName?: string;
  isError?: boolean;
  status?: { type?: string; reason?: string };
};

/**
 * Walk message parts left-to-right and group consecutive collapsible tool-call
 * parts into runs. Every other part becomes its own `passthrough` segment.
 *
 * Collapsibility is derived from each tool's `display.progress` on its
 * model-context entry (see {@link useIsCollapsibleTool}), OR from the part
 * being in a terminal failure state (see {@link isErroredToolPart}). Errored
 * tool calls are folded into the run accordion regardless of their declared
 * `display.progress` — once a tool has failed, its inline output is no longer
 * useful and visually belongs with the other tool-call rows.
 *
 * Takes `parts` as a parameter rather than subscribing internally so the
 * caller can share a single `useAuiState((s) => s.message.parts)` subscription
 * with the rest of the render — the previous shape ran the selector twice
 * per render (once here, once at the call site), which fired on every chunk
 * during streaming.
 */
function useGroupedParts(parts: readonly GroupablePart[]): PartSegment[] {
  const isCollapsibleTool = useIsCollapsibleTool();
  const isCollapsible = (part: GroupablePart): boolean => {
    if (part.type !== 'tool-call') {
      return false;
    }
    return isCollapsibleTool(part.toolName) || isErroredToolPart(part);
  };

  return groupParts(parts, isCollapsible);
}

/**
 * Top-level message-parts renderer for assistant messages.
 *
 * Replaces the previous single `<MessagePrimitive.Parts>` call. Splits parts
 * into segments — consecutive collapsible tool calls become one
 * `CollapsibleToolRun`; every other part passes through unchanged via
 * `MessagePrimitive.PartByIndex` with the same component overrides the thread
 * has always used (`Text: AssistantBubble`, `tools.Fallback: ToolDisplay`).
 */
export function CollapsibleMessageParts() {
  const parts = useAuiState((state) => state.message.parts);
  const segments = useGroupedParts(parts);
  const isRunning = useAuiState((state) => state.message.status?.type === 'running');

  if (!segments.length && isRunning) {
    return <ProgressBubble />;
  }

  return segments.map((segment, segmentIndex) => {
    if (segment.kind === 'collapsible-run') {
      const lastIndex = segment.indices.at(-1);
      const lastPart = lastIndex === undefined ? undefined : parts.at(lastIndex);
      const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
      const isLastPart = segment.indices.some((i) => i >= parts.length - 1);
      return (
        <React.Fragment
          // biome-ignore lint/suspicious/noArrayIndexKey: Segment order is stable within a message render.
          key={`run-${segmentIndex}`}
        >
          <CollapsibleToolRun indices={segment.indices} />
          {isRunning && isLastPart && <ProgressBubble toolPart={toolPart} />}
        </React.Fragment>
      );
    }

    const lastPart = parts.at(-1);
    const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
    return (
      <React.Fragment key={`part-${segment.index}`}>
        <MessagePrimitive.PartByIndex
          components={{
            Text: AssistantBubble,
            tools: { Fallback: ToolDisplay },
          }}
          index={segment.index}
        />
        {isRunning && segmentIndex === segments.length - 1 && (
          <ProgressBubble fallbackComponent={null} toolPart={toolPart} />
        )}
      </React.Fragment>
    );
  });
}
